import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import React from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { TotalPortfolioModal } from '@pages/Traders-Room/Wallets/modals/TotalPortfolio.modal'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Title2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Link } from '@ui/components/Link'
import { BellIcon } from '@ui/icons/Bell.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { WalletIcon } from '@ui/icons/Wallet.icon'
import { Skeleton } from '@ui/v2/Skeleton'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useNotificationUnread } from '@utils/notifications'
import { useBasePath } from '@utils/path'

import { TotalWalletBalanceDto } from '@model/TotalWalletBalanceDto'

import { TMEntityLogoFactory } from '@assets/logo/TickmillLogoIcons'

import { AnimatedNumber } from '@shared/animations/AnimatedNumber'
import { Box } from '@shared/components/Box'

import { useHideSensitiveData } from '../../menuSections/topLevelMenus/useHideSensitiveData'
import { ProfileMenu } from './ProfileMenu'
import { ThemeMenu } from './ThemeMenu'

import styles from './TopBar.module.scss'

export const TopBarFull: React.FC = () => {
  const navigate = useNavigate()
  const { hasUnread } = useNotificationUnread()
  const hideSensitiveData = useHideSensitiveData()
  const { redirectionLink } = useBasePath(true)
  const { account } = useAccountReadContext()
  const apiClient = useApiClient(ClientApiClient)
  const totalPortfolioValueModal = useModal(TotalPortfolioModal)

  const tradingAccountTotals = useQuery({
    queryKey: queryKeys.tradingAccount.totals(account?.displayCurrency?.id ?? 'USD'),
    queryFn: () => apiClient.getTradingAccountsTotals(),
    refetchOnMount: 'always',
    enabled: !!account?.displayCurrency?.id,
  })

  const totalWalletBalance = useQuery({
    queryKey: queryKeys.walletExtended.totalBalance(account?.displayCurrency?.id ?? 'USD'),
    queryFn: (): Promise<TotalWalletBalanceDto | null> =>
      apiClient.getTotalBalanceByCurrencyBothProducts(account?.displayCurrency?.id ?? 'USD'),
    refetchOnMount: 'always',
    enabled: !!account?.displayCurrency?.id,
  })
  const totalBalanceValue =
    (totalWalletBalance.data?.balance || 0) +
    (tradingAccountTotals.data?.totalBalanceInDisplayCurrency || 0)
  const isTotalBalanceReady = totalWalletBalance.isFetched && tradingAccountTotals.isFetched
  const isTotalBalanceSyncing = totalWalletBalance.isFetching || tradingAccountTotals.isFetching

  return (
    <header className={classNames(styles.topBar, styles.topBarFull)}>
      <div className={styles.logo}>
        <Link to={redirectionLink}>
          <TMEntityLogoFactory />
        </Link>
      </div>

      <div className={styles.spacer} />

      <div className={styles.actions}>
        <Box flex align='center' gap='x8'>
          <WalletIcon color={'text-white'} />
          {isTotalBalanceReady ? (
            <Title2 semibold>
              <SensitiveData>
                ≈
                <AnimatedNumber
                  value={totalBalanceValue}
                  currency={account?.displayCurrency?.id}
                  isReady={isTotalBalanceReady && !isTotalBalanceSyncing}
                />
              </SensitiveData>
            </Title2>
          ) : (
            <Skeleton as='span' inverse width={120} height={24} variant='rounded' />
          )}
          <ActionIcon
            icon={{ icon: InfoIcon, color: 'text-white' }}
            size='x24'
            onClick={() => totalPortfolioValueModal.open()}
          />
        </Box>

        {hideSensitiveData.icon && (
          <Box p='x4' flex align='center' justify='center'>
            <ActionIcon
              icon={{ icon: hideSensitiveData.icon, color: 'text-white' }}
              size='x24'
              onClick={hideSensitiveData.onClick}
              aria-label={hideSensitiveData.label}
            />
          </Box>
        )}

        <ThemeMenu />

        <Box p='x4' flex align='center' justify='center'>
          <ActionIcon
            icon={{ icon: BellIcon, color: 'text-white' }}
            size='x24'
            pimple={hasUnread ? { size: 'x6' } : undefined}
            onClick={() => navigate('/profile/notifications')}
            aria-label='Notifications'
          />
        </Box>

        <ProfileMenu />
      </div>
    </header>
  )
}
